import type { ButtonHTMLAttributes, FunctionComponent, ReactNode } from 'react';

type Variant = 'default' | 'primary' | 'ghost' | 'accent' | 'danger';
type Size = 'default' | 'small';

const base =
  'inline-flex items-center gap-[6px] whitespace-nowrap rounded-sm font-sans font-medium transition-[background,border-color,color,box-shadow,transform] duration-100 ease-out active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0';

const sizeClasses: Record<Size, string> = {
  default: 'h-[28px] py-[6px] px-[11px] text-[12.5px]',
  small: 'h-[26px] py-[4px] px-[9px] text-[12px]',
};

const variantClasses: Record<Variant, string> = {
  default:
    'bg-bg-2 text-ink border border-line-2 shadow-1 hover:bg-bg-hover hover:border-line-3 active:shadow-none',
  primary:
    'bg-acc text-white border border-transparent shadow-[0_1px_2px_rgba(91,69,179,0.28)] hover:bg-acc-2 active:shadow-[inset_0_0_0_1px_rgba(91,69,179,0.3)]',
  ghost: 'bg-transparent text-ink-2 border border-transparent hover:bg-bg-hover hover:text-ink',
  accent: 'bg-transparent text-acc border border-transparent hover:bg-acc-soft hover:text-acc-2',
  danger: 'bg-rose text-white border border-rose hover:brightness-95',
};

export function buttonClassName({
  variant = 'default',
  size = 'default',
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}): string {
  return [base, sizeClasses[size], variantClasses[variant], className].filter(Boolean).join(' ');
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
};

export const Button: FunctionComponent<Props> = ({
  variant = 'default',
  size = 'default',
  className,
  loading = false,
  disabled,
  children,
  ...rest
}) => {
  return (
    <button
      className={buttonClassName({ variant, size, className })}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          className="animate-spin"
          aria-hidden
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ) : null}
      {children}
    </button>
  );
};
